import { AuditLogScopeType, ResourceType } from "@common/types/auditLog";
import { HistoryField, HistoryGroupBy } from "./history/history-fields";
import {
    buildInternalSourceTypeByPlatformTwin,
    expandWithPlatformTwins,
    withPlatformTwinConfigs,
    withPlatformTwinTemplates,
} from "./history-platform-twins";
import { catalogItemSources } from "./history/catalog-item-sources";
import { invoiceTemplateSources } from "./history/invoice-template-sources";
import {
    deriveHistorySourceTypeConfigs,
    ORGANIZATION_MEMBER_SOURCE_TYPE_TEMPLATES,
    organizationMemberSources,
    type DerivedHistorySourceTypeConfigs,
} from "./history/organization-history-sources";
import { scheduleSources } from "./history/schedule-sources";
import { reportSettingsSources } from "./history/report-settings-sources";
import { transactionSources } from "./history/transaction-sources";
import type {
    HistorySourceTypeConfig,
    ResourceTypeConfig,
    SourceTypeTemplate,
    SourceTypeTemplateValues,
} from "./history/history-source-config";
import { IN_APP_HISTORY_SOURCE_TYPE_TEMPLATES } from "./history/source-type-templates";
import { enrichmentSources } from "./history/enrichment-sources";
import { integrationSources } from "./history/integration-sources";
import { taskSources } from "./history/task-sources";
import {
    reconciliationSources,
    financialAccountSources,
} from "./history/financial-account-sources";
import { classSources } from "./history/class-sources";
import { accountSources, coaTemplateApplySources } from "./history/coa-sources";
import {
    documentSources,
    receiptMatchSources,
    financialDocumentDetachSource,
} from "./history/document-sources";
import { counterpartySources } from "./history/counterparty-sources";
import {
    transactionRuleSources,
    transactionRuleGroupSources,
} from "./history/transaction-rule-sources";
import { journalEntrySources } from "./history/journal-entry-sources";
import { billSources, invoiceSources } from "./history/billing-sources";

export {
    COA_ACTIVITY_SOURCE_TYPES,
    COA_CASCADED_UPDATE_SOURCE_TYPES,
    COA_NONE_SOURCE_TYPES,
    COA_RESOURCE_TYPES,
    COA_TEMPLATE_ACTIVITY_SOURCE_TYPES,
} from "./history/coa-sources";
export type {
    DiffValueHistorySourceTypeConfig,
    HistorySourceTypeConfig,
    ResourceTypeConfig,
    SourceTypeTemplate,
    SourceTypeTemplateValues,
    StandardHistorySourceTypeConfig,
} from "./history/history-source-config";
export {
    COA_HIDDEN_FIELDS,
    DOCUMENT_DISPLAY_FIELDS,
    WORK_ITEM_HISTORY_FIELDS,
} from "./history/source-fields";
export {
    applyTransactionUpdate,
    checkAndMergeSimilarCounterparties,
    normalizeCategorizationForTheSameTransactions,
} from "./history/enrichment-activity-source-types";

export {
    deriveHistorySourceTypeConfigs,
    ORGANIZATION_MEMBER_SOURCE_TYPE_TEMPLATES,
    organizationMemberSources,
    type DerivedHistorySourceTypeConfigs,
};
export {
    SCHEDULE_ACTIVITY_SOURCES,
    SCHEDULE_ACTIVITY_SOURCE_TYPES,
    SCHEDULE_RESOURCE_TYPES,
} from "./history/schedule-sources";
export type { ScheduleActivitySource } from "./history/schedule-sources";

export { HistoryField, HistoryGroupBy };
export { HISTORY_FIELD_LABELS } from "./history/history-fields";

const IN_APP_HISTORY_SOURCE_TYPE_CONFIGS: HistorySourceTypeConfig[] = [
    ...transactionSources,
    ...enrichmentSources,
    ...integrationSources,
    ...taskSources,
    ...reconciliationSources,
    ...financialAccountSources,
    ...classSources,
    ...catalogItemSources,
    ...accountSources,
    ...counterpartySources,
    ...transactionRuleSources,
    ...transactionRuleGroupSources,
    ...journalEntrySources,
    ...receiptMatchSources,
    financialDocumentDetachSource,
    ...billSources,
    ...invoiceSources,
    ...invoiceTemplateSources,
    ...documentSources,
    ...coaTemplateApplySources,
    ...scheduleSources,
    ...reportSettingsSources,
    ...organizationMemberSources,
];

export const HISTORY_SOURCE_TYPE_CONFIGS: HistorySourceTypeConfig[] =
    withPlatformTwinConfigs(IN_APP_HISTORY_SOURCE_TYPE_CONFIGS);

export const HISTORY_SOURCE_TYPE_TEMPLATES: Record<string, SourceTypeTemplate> =
    withPlatformTwinTemplates(
        IN_APP_HISTORY_SOURCE_TYPE_TEMPLATES,
        IN_APP_HISTORY_SOURCE_TYPE_CONFIGS,
    );

const INTERNAL_SOURCE_TYPE_BY_PLATFORM_TWIN =
    buildInternalSourceTypeByPlatformTwin(IN_APP_HISTORY_SOURCE_TYPE_CONFIGS);

export const PLATFORM_HISTORY_SOURCE_TYPES: readonly string[] = [
    ...INTERNAL_SOURCE_TYPE_BY_PLATFORM_TWIN.keys(),
];

export function toInternalHistorySourceType(sourceType: string): string {
    return INTERNAL_SOURCE_TYPE_BY_PLATFORM_TWIN.get(sourceType) ?? sourceType;
}

export function withPlatformTwinSourceTypes(
    sourceTypes: readonly string[],
): string[] {
    return expandWithPlatformTwins(
        sourceTypes.map((sourceType) => ({
            sourceType,
            platformTwins:
                IN_APP_HISTORY_SOURCE_TYPE_CONFIGS.find(
                    (config) => config.sourceType === sourceType,
                )?.platformTwins ?? null,
        })),
    );
}

export const DERIVED_HISTORY_SOURCE_TYPE_CONFIGS: DerivedHistorySourceTypeConfigs =
    deriveHistorySourceTypeConfigs(
        HISTORY_SOURCE_TYPE_CONFIGS,
        AuditLogScopeType.WORKSPACE,
    );

export const DERIVED_ORGANIZATION_HISTORY_SOURCE_TYPE_CONFIGS: DerivedHistorySourceTypeConfigs =
    deriveHistorySourceTypeConfigs(
        HISTORY_SOURCE_TYPE_CONFIGS,
        AuditLogScopeType.ORGANIZATION,
    );

export const HISTORY_SOURCE_TYPE_WHITELIST =
    DERIVED_HISTORY_SOURCE_TYPE_CONFIGS.sourceTypeWhitelist;

export const HISTORY_GROUPED_SOURCE_TYPES =
    DERIVED_HISTORY_SOURCE_TYPE_CONFIGS.groupedSourceTypes;

export const HISTORY_NON_GROUPED_SOURCE_TYPES =
    DERIVED_HISTORY_SOURCE_TYPE_CONFIGS.nonGroupedSourceTypes;

export const HISTORY_SOURCE_GROUPED_SOURCE_TYPES =
    DERIVED_HISTORY_SOURCE_TYPE_CONFIGS.sourceGroupedSourceTypes;

export const HISTORY_SOURCE_GROUPED_CONFIGS =
    DERIVED_HISTORY_SOURCE_TYPE_CONFIGS.sourceGroupedConfigs;

export const HISTORY_DIFF_VALUE_GROUPED_SOURCE_TYPES =
    DERIVED_HISTORY_SOURCE_TYPE_CONFIGS.diffValueGroupedSourceTypes;

export const HISTORY_DIFF_VALUE_GROUPED_CONFIGS =
    DERIVED_HISTORY_SOURCE_TYPE_CONFIGS.diffValueGroupedConfigs;

export const HISTORY_SOURCE_COUNT_DISTINCT_CONFIGS =
    DERIVED_HISTORY_SOURCE_TYPE_CONFIGS.sourceCountDistinctConfigs;

export const HISTORY_RESOURCE_TYPE_WHITELIST =
    DERIVED_HISTORY_SOURCE_TYPE_CONFIGS.resourceTypeWhitelist;

export const HISTORY_FIELD_WHITELIST =
    DERIVED_HISTORY_SOURCE_TYPE_CONFIGS.fieldWhitelist;

export function getHistoryConfigForSourceAndResourceType(
    sourceType: string,
    resourceType: ResourceType,
): ResourceTypeConfig | undefined {
    const configs =
        DERIVED_HISTORY_SOURCE_TYPE_CONFIGS.getResourceTypeConfigsForSource(
            sourceType,
        );
    return configs.find((rtConfig) => rtConfig.resourceType === resourceType);
}

export function getResourceTypesForSource(sourceType: string): ResourceType[] {
    const config = DERIVED_HISTORY_SOURCE_TYPE_CONFIGS.sourceTypeConfigs.find(
        (c) => c.sourceType === sourceType,
    );

    return (
        config?.resourceTypeConfigs.map((rtConfig) => rtConfig.resourceType) ??
        []
    );
}

export function shouldResolveEntities(
    sourceType: string,
    resourceType: ResourceType,
): boolean {
    const config = getHistoryConfigForSourceAndResourceType(
        sourceType,
        resourceType,
    );
    return config?.resolveEntity ?? false;
}

export function getResolveEntityResourceTypes(
    sourceType: string,
): ResourceType[] {
    return DERIVED_HISTORY_SOURCE_TYPE_CONFIGS.getResourceTypeConfigsForSource(
        sourceType,
    )
        .filter((rtConfig) => rtConfig.resolveEntity)
        .map((rtConfig) => rtConfig.resourceType);
}

export function getSourceTypeTemplate(
    sourceType: string,
    resourceType?: ResourceType,
    fields?: string[],
): SourceTypeTemplateValues | undefined {
    const template = HISTORY_SOURCE_TYPE_TEMPLATES[sourceType];

    if (!template) {
        return undefined;
    }

    if (fields?.length && template.fieldOverrides) {
        const field = fields[0] as HistoryField;
        if (template.fieldOverrides[field]) {
            return template.fieldOverrides[field];
        }
    }

    if (resourceType && template.resourceTypeOverrides?.[resourceType]) {
        return template.resourceTypeOverrides[resourceType];
    }

    const { verb, pluralVerb, noun, suffix, skipFieldLabel } = template;
    if (verb === undefined || noun === undefined) {
        return undefined;
    }

    return { verb, pluralVerb, noun, suffix, skipFieldLabel };
}

export function getAllResourceTypeConfigsForSource(
    sourceType: string,
): ResourceTypeConfig[] {
    return DERIVED_HISTORY_SOURCE_TYPE_CONFIGS.getResourceTypeConfigsForSource(
        sourceType,
    );
}

export function getHistorySourceTypeConfig(
    sourceType: string,
): HistorySourceTypeConfig | undefined {
    return DERIVED_HISTORY_SOURCE_TYPE_CONFIGS.sourceTypeConfigs.find(
        (c) => c.sourceType === sourceType && c.enabled,
    );
}
